import Toast from 'react-native-toast-message'
import ErrorHandler from '../utils/ErrorHandler';
import { getCurrentOrgID } from './organization';
import { removeStorageFile } from '../utils/FileManager';
import { supabase } from '../data/client';

export const STORAGE_LIMIT_BYTES = 10 * 1024 * 1024 * 1024; // 10 GB

export const getOrgStorageUsage = async () => {
    const orgID = await getCurrentOrgID();

    const { data, error } = await supabase.rpc('get_org_storage_usage', { p_org_id: orgID });

    if (error) {
        ErrorHandler(Error(error.message), { query: 'getOrgStorageUsage', orgID }, 'Could not fetch storage usage');
        return { usedBytes: 0, limitBytes: STORAGE_LIMIT_BYTES, remainingBytes: STORAGE_LIMIT_BYTES };
    }

    const usedBytes = Number(data) || 0;
    return {
        usedBytes,
        limitBytes: STORAGE_LIMIT_BYTES,
        remainingBytes: Math.max(0, STORAGE_LIMIT_BYTES - usedBytes),
    };
};

// Reads
export const getRootFolder = async ({ scope, ownerID }) => {
    const orgID = await getCurrentOrgID();
    
    const query = supabase
        .from('Folders')
        .select('*')
        .eq('scope', scope)
        .eq('organizationID', orgID)
        .eq('isRoot', true);

    if (ownerID) {
        query.eq('ownerID', ownerID);
    } else {
        query.is('ownerID', null);
    }

    const { error, data } = await query.maybeSingle();

    if (error) {
        ErrorHandler(Error(error.message), { query: 'getRootFolder', scope, ownerID }, 'Could not get root folder');
        return null;
    }

    return data;
};

export const getFolderContents = async ({ folderID, scope, ownerID }) => {
    const { error, data } = await supabase.rpc('list_folder_contents', {
        p_folder_id: folderID === 'root' ? null : folderID,
        p_scope: scope,
        p_owner_id: ownerID || null
    });

    if (error) {
        ErrorHandler(Error(error.message), { query: 'getFolderContents', folderID, scope, ownerID }, 'Could not get folder contents');
        return null;
    }

    // RPC returns { folder, subfolders, files } — normalize to { folders, files }
    return {
        folders: data?.subfolders || [],
        files: data?.files || [],
        folder: data?.folder || null,
    };
};

export const searchFiles = async ({ query, scope, ownerID, rootFolderID }) => {
    const { error, data } = await supabase.rpc('search_files_and_folders', {
        p_query: query,
        p_scope: scope,
        p_owner_id: ownerID || null,
        p_root_folder_id: rootFolderID || null,
        p_limit: 50
    });

    if (error) {
        ErrorHandler(Error(error.message), { query: 'searchFiles', searchQuery: query, scope, ownerID }, 'Search failed');
        return { folders: [], files: [] };
    }

    return data;
};

export const getFolderBreadcrumbs = async ({ folderID }) => {
    const { error, data } = await supabase.rpc('get_folder_breadcrumbs', {
        p_folder_id: folderID
    });

    if (error) {
        ErrorHandler(Error(error.message), { query: 'getFolderBreadcrumbs', folderID }, 'Could not get breadcrumbs');
        return [];
    }

    return data;
};

export const getFolderTree = async ({ scope, ownerID }) => {
    const orgID = await getCurrentOrgID();
    
    const query = supabase
        .from('Folders')
        .select('id, name, parentFolderID, depth, isRoot')
        .eq('scope', scope)
        .eq('organizationID', orgID)
        .order('depth', { ascending: true })
        .order('name', { ascending: true });

    if (ownerID) {
        query.eq('ownerID', ownerID);
    } else {
        query.is('ownerID', null);
    }

    const { error, data } = await query;

    if (error) {
        ErrorHandler(Error(error.message), { query: 'getFolderTree', scope, ownerID }, 'Could not get folder tree');
        return [];
    }

    return data;
};

// Helper: Check for duplicate folder name in parent folder
const checkFolderNameExists = async ({ name, parentFolderID, scope, ownerID, excludeID = null }) => {
    const orgID = await getCurrentOrgID();
    
    let query = supabase
        .from('Folders')
        .select('id')
        .eq('name', name)
        .eq('scope', scope)
        .eq('organizationID', orgID)
        .eq('parentFolderID', parentFolderID === 'root' ? null : (parentFolderID || null));

    if (ownerID) {
        query.eq('ownerID', ownerID);
    } else {
        query.is('ownerID', null);
    }

    if (excludeID) {
        query.neq('id', excludeID);
    }

    const { data } = await query.maybeSingle();
    return data !== null;
};

// Helper: Check for duplicate file name in folder
const checkFileNameExists = async ({ filename, folderID, scope, ownerID, excludeID = null }) => {
    const orgID = await getCurrentOrgID();
    
    let query = supabase
        .from('Files')
        .select('id')
        .eq('filename', filename)
        .eq('scope', scope)
        .eq('organizationID', orgID)
        .eq('folderID', folderID === 'root' ? null : folderID);

    if (ownerID) {
        query.eq('ownerID', ownerID);
    } else {
        query.is('ownerID', null);
    }

    if (excludeID) {
        query.neq('id', excludeID);
    }

    const { data } = await query.maybeSingle();
    return data !== null;
};

// Folder mutations
export const createFolder = async ({ name, parentFolderID, scope, ownerID, roleIDs = [] }) => {
    const orgID = await getCurrentOrgID();
    const { data: { user } } = await supabase.auth.getUser();

    const { data: regUser } = await supabase
        .from('RegisteredUsersOrganizations')
        .select('personID')
        .eq('userID', user.id)
        .eq('organizationID', orgID)
        .maybeSingle();

    if (!regUser?.personID) {
        Toast.show({ type: 'error', text2: 'Could not find user profile' });
        return null;
    }

    // Check for duplicate name
    const nameExists = await checkFolderNameExists({ name, parentFolderID, scope, ownerID });
    if (nameExists) {
        Toast.show({ type: 'error', text2: 'A folder with this name already exists' });
        return null;
    }

    const { error, data } = await supabase
        .from('Folders')
        .insert({
            name,
            parentFolderID: parentFolderID === 'root' ? null : (parentFolderID || null),
            scope,
            ownerID: ownerID || null,
            organizationID: orgID,
            createdByID: regUser.personID
        })
        .select()
        .single();

    if (error) {
        ErrorHandler(Error(error.message), { mutation: 'createFolder', name, parentFolderID, scope, ownerID }, 'Could not create folder');
        return null;
    }

    if (roleIDs.length > 0) {
        await supabase.from('FolderRoles').insert(roleIDs.map(roleID => ({ folderID: data.id, roleID, organizationID: orgID })));
    }

    Toast.show({ type: 'success', text2: 'Folder created successfully' });
    return data;
};

export const renameFolder = async ({ id, name }) => {
    // Get folder to check parent
    const { data: folder } = await supabase
        .from('Folders')
        .select('parentFolderID, scope, ownerID')
        .eq('id', id)
        .single();

    if (!folder) {
        Toast.show({ type: 'error', text2: 'Folder not found' });
        return null;
    }

    // Check for duplicate name in same parent
    const nameExists = await checkFolderNameExists({ 
        name, 
        parentFolderID: folder.parentFolderID, 
        scope: folder.scope, 
        ownerID: folder.ownerID,
        excludeID: id 
    });
    if (nameExists) {
        Toast.show({ type: 'error', text2: 'A folder with this name already exists' });
        return null;
    }

    const { error, data } = await supabase
        .from('Folders')
        .update({ name })
        .eq('id', id)
        .select()
        .single();

    if (error) {
        ErrorHandler(Error(error.message), { mutation: 'renameFolder', id, name }, 'Could not rename folder');
        return null;
    }

    Toast.show({ type: 'success', text2: 'Folder renamed successfully' });
    return data;
};

export const updateFolder = async ({ id, name, roleIDs = [] }) => {
    const orgID = await getCurrentOrgID();

    const { data: folder } = await supabase
        .from('Folders')
        .select('parentFolderID, scope, ownerID')
        .eq('id', id)
        .single();

    if (!folder) {
        Toast.show({ type: 'error', text2: 'Folder not found' });
        return null;
    }

    const nameExists = await checkFolderNameExists({
        name,
        parentFolderID: folder.parentFolderID,
        scope: folder.scope,
        ownerID: folder.ownerID,
        excludeID: id
    });
    if (nameExists) {
        Toast.show({ type: 'error', text2: 'A folder with this name already exists' });
        return null;
    }

    const { error, data } = await supabase
        .from('Folders')
        .update({ name })
        .eq('id', id)
        .select()
        .single();

    if (error) {
        ErrorHandler(Error(error.message), { mutation: 'updateFolder', id, name }, 'Could not update folder');
        return null;
    }

    await supabase.from('FolderRoles').delete().eq('folderID', id);

    if (roleIDs.length > 0) {
        await supabase.from('FolderRoles').insert(roleIDs.map(roleID => ({ folderID: id, roleID, organizationID: orgID })));
    }

    Toast.show({ type: 'success', text2: 'Folder updated successfully' });
    return data;
};

export const moveFolder = async ({ id, newParentFolderID }) => {
    // Get folder details
    const { data: folder } = await supabase
        .from('Folders')
        .select('path, name, scope, ownerID')
        .eq('id', id)
        .single();

    if (!folder) {
        Toast.show({ type: 'error', text2: 'Folder not found' });
        return null;
    }

    // Check for cycle (moving into own descendant)
    if (newParentFolderID) {
        const { data: newParent } = await supabase
            .from('Folders')
            .select('path')
            .eq('id', newParentFolderID)
            .single();

        if (newParent && folder?.path && newParent.path.startsWith(folder.path)) {
            Toast.show({ type: 'error', text2: 'Cannot move a folder into its own subfolder' });
            return null;
        }

        // Check for duplicate name in destination
        const nameExists = await checkFolderNameExists({ 
            name: folder.name, 
            parentFolderID: newParentFolderID, 
            scope: folder.scope, 
            ownerID: folder.ownerID,
            excludeID: id 
        });
        if (nameExists) {
            Toast.show({ type: 'error', text2: 'A folder with this name already exists in the destination' });
            return null;
        }
    }

    const { error, data } = await supabase
        .from('Folders')
        .update({ parentFolderID: newParentFolderID === 'root' ? null : newParentFolderID })
        .eq('id', id)
        .select()
        .single();

    if (error) {
        ErrorHandler(Error(error.message), { mutation: 'moveFolder', id, newParentFolderID }, 'Could not move folder');
        return null;
    }

    // Move all storage objects under this folder
    // This is complex - for now we'll skip storage moves and rely on the DB move
    // A future enhancement would recursively move storage objects

    Toast.show({ type: 'success', text2: 'Folder moved successfully' });
    return data;
};

export const deleteFolder = async ({ id }) => {
    // Get all descendant files using ltree query
    const { data: folder } = await supabase
        .from('Folders')
        .select('path')
        .eq('id', id)
        .single();

    if (!folder) {
        return null;
    }

    // Get all files in this subtree
    const { data: descendantFiles } = await supabase
        .from('Files')
        .select('id, key')
        .eq('organizationID', await getCurrentOrgID())
        .filter('path', 'cs', folder.path);

    // Delete all storage objects
    for (const file of descendantFiles || []) {
        await removeStorageFile(file.key);
    }

    // Delete folder (cascade will handle subfolders and files)
    const { error } = await supabase
        .from('Folders')
        .delete()
        .eq('id', id);

    if (error) {
        ErrorHandler(Error(error.message), { mutation: 'deleteFolder', id }, 'Could not delete folder');
        return null;
    }

    Toast.show({ type: 'success', text2: 'Folder deleted successfully' });
    return (descendantFiles || []).map(f => f.id);
};

// File mutations
export const createFileRecord = async ({ folderID, scope, ownerID, filename, key, size, mimeType, uploadedByID }) => {
    const orgID = await getCurrentOrgID();

    const { error, data } = await supabase
        .from('Files')
        .insert({
            filename,
            key,
            size,
            mimeType,
            scope,
            ownerID: ownerID || null,
            folderID: folderID === 'root' ? null : folderID,
            organizationID: orgID,
            uploadedByID
        })
        .select()
        .single();

    if (error) {
        ErrorHandler(Error(error.message), { mutation: 'createFileRecord', filename, key }, 'Could not create file record');
        return null;
    }

    return data;
};

export const renameFile = async ({ id, newFilename }) => {
    // Get file to check folder
    const { data: file } = await supabase
        .from('Files')
        .select('folderID, scope, ownerID')
        .eq('id', id)
        .single();

    if (!file) {
        Toast.show({ type: 'error', text2: 'File not found' });
        return null;
    }

    // Check for duplicate filename in same folder
    const nameExists = await checkFileNameExists({ 
        filename: newFilename, 
        folderID: file.folderID, 
        scope: file.scope, 
        ownerID: file.ownerID,
        excludeID: id 
    });
    if (nameExists) {
        Toast.show({ type: 'error', text2: 'A file with this name already exists' });
        return null;
    }

    const { error, data } = await supabase
        .from('Files')
        .update({ filename: newFilename })
        .eq('id', id)
        .select()
        .single();

    if (error) {
        ErrorHandler(Error(error.message), { mutation: 'renameFile', id, newFilename }, 'Could not rename file');
        return null;
    }

    Toast.show({ type: 'success', text2: 'File renamed successfully' });
    return data;
};

export const moveFile = async ({ id, newFolderID }) => {
    // Get file details
    const { data: file } = await supabase
        .from('Files')
        .select('filename, scope, ownerID')
        .eq('id', id)
        .single();

    if (!file) {
        Toast.show({ type: 'error', text2: 'File not found' });
        return null;
    }

    // Check for duplicate filename in destination folder
    const nameExists = await checkFileNameExists({ 
        filename: file.filename, 
        folderID: newFolderID, 
        scope: file.scope, 
        ownerID: file.ownerID,
        excludeID: id 
    });
    if (nameExists) {
        Toast.show({ type: 'error', text2: 'A file with this name already exists in the destination' });
        return null;
    }

    const { error, data } = await supabase
        .from('Files')
        .update({ folderID: newFolderID === 'root' ? null : newFolderID })
        .eq('id', id)
        .select()
        .single();

    if (error) {
        ErrorHandler(Error(error.message), { mutation: 'moveFile', id, newFolderID }, 'Could not move file');
        return null;
    }

    // Move storage object - for now skip, would need to compute new key
    // Future enhancement: move storage object to new path

    Toast.show({ type: 'success', text2: 'File moved successfully' });
    return data;
};

export const deleteFile = async ({ id, key }) => {
    await removeStorageFile(key);

    const { error } = await supabase
        .from('Files')
        .delete()
        .eq('id', id);

    if (error) {
        ErrorHandler(Error(error.message), { mutation: 'deleteFile', id, key }, 'Could not delete file');
        return null;
    }

    Toast.show({ type: 'success', text2: 'File deleted successfully' });
    return true;
};

// Bulk operations
export const bulkDelete = async ({ folderIDs, fileIDs }) => {
    const deletedFileIDs = [...(fileIDs || [])];

    // Get all files to delete for storage cleanup
    const { data: files } = await supabase
        .from('Files')
        .select('key')
        .in('id', fileIDs);

    for (const file of files || []) {
        await removeStorageFile(file.key);
    }

    // Get all folders to delete for subtree cleanup
    for (const folderID of folderIDs || []) {
        const folderFileIDs = await deleteFolder({ id: folderID, noMessage: true });
        if (folderFileIDs) {
            deletedFileIDs.push(...folderFileIDs);
        }
    }

    // Delete files
    const { error: fileError } = await supabase
        .from('Files')
        .delete()
        .in('id', fileIDs);

    if (fileError) {
        ErrorHandler(Error(fileError.message), { mutation: 'bulkDelete', fileIDs }, 'Could not delete files');
        return null;
    }

    Toast.show({ type: 'success', text2: 'Items deleted successfully' });
    return { success: true, deletedFileIDs: [...new Set(deletedFileIDs)] };
};

export const bulkMove = async ({ folderIDs, fileIDs, destinationFolderID }) => {
    // Move folders
    for (const folderID of folderIDs || []) {
        await moveFolder({ id: folderID, newParentFolderID: destinationFolderID });
    }

    // Move files
    for (const fileID of fileIDs || []) {
        await moveFile({ id: fileID, newFolderID: destinationFolderID });
    }

    Toast.show({ type: 'success', text2: 'Items moved successfully' });
    return true;
};
